import axios from "axios";

import { API_URL } from "../utils/Constant";
import { getViewAsPerStatusCode } from "../utils/helpers";

export const CUSTOMER_GROUP_TYPE = "customer-specific";

/** /testsuggestions/ `category` query values (BE default: customer-mapping). */
export const TEST_SUGGESTION_CATEGORY = {
  CUSTOMER_MAPPING: "customer-mapping",
  TEST_DIRECTORY: "test-directory",
};

const isTestDirectoryCategory = (category) => {
  const key = String(category || "").trim().toLowerCase();
  return key === TEST_SUGGESTION_CATEGORY.TEST_DIRECTORY || key === "test_directory";
};

const asTestList = (value) => (Array.isArray(value) ? value : []);

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const readApiResultList = (data = {}) => {
  const raw = data.result ?? data.results;
  return Array.isArray(raw) ? raw : [];
};

/**
 * Read customer-enabled vs unlockable lists from /testsuggestions/.
 * Prefer `category` (flat result); nested split payloads still supported.
 */
const readSuggestionLists = (data = {}, category) => {
  const raw = data.result ?? data.results;
  const normalizedCategory = String(category || "").trim().toLowerCase();

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const nestedCustomerKey = ["customer_specific", "customerTests", "customer_tests"]
      .find((key) => hasOwn(raw, key));
    const nestedDirectoryKey = ["test_directory", "directoryTests", "directory_tests"]
      .find((key) => hasOwn(raw, key));
    const nestedRequestedKey = ["requested_tests", "requested_unlock_tests", "pending_unlock_tests"]
      .find((key) => hasOwn(raw, key));

    if (nestedCustomerKey != null || nestedDirectoryKey != null || nestedRequestedKey != null) {
      return {
        customerTests: nestedCustomerKey != null ? asTestList(raw[nestedCustomerKey]) : [],
        directoryTests: nestedDirectoryKey != null ? asTestList(raw[nestedDirectoryKey]) : [],
        requestedDirectoryTests: nestedRequestedKey != null ? asTestList(raw[nestedRequestedKey]) : [],
        hasSplitLists: true,
      };
    }
  }

  if (Array.isArray(raw)) {
    if (isTestDirectoryCategory(normalizedCategory)) {
      return {
        customerTests: [],
        directoryTests: raw,
        requestedDirectoryTests: [],
        hasSplitLists: true,
      };
    }
    if (normalizedCategory === TEST_SUGGESTION_CATEGORY.CUSTOMER_MAPPING) {
      return { customerTests: raw, directoryTests: [], hasSplitLists: true };
    }
    return {
      customerTests: raw,
      directoryTests: raw,
      requestedDirectoryTests: [],
      hasSplitLists: false,
    };
  }

  const customerKey = ["customerTests", "customer_tests", "customer_enabled_tests"]
    .find((key) => hasOwn(data, key));
  const directoryKey = ["directoryTests", "directory_tests", "unlockable_tests", "test_directory"]
    .find((key) => hasOwn(data, key));
  const requestedKey = ["requested_tests", "requested_unlock_tests", "pending_unlock_tests"]
    .find((key) => hasOwn(data, key));

  if (customerKey != null || directoryKey != null || requestedKey != null) {
    return {
      customerTests: customerKey != null ? asTestList(data[customerKey]) : [],
      directoryTests: directoryKey != null ? asTestList(data[directoryKey]) : [],
      requestedDirectoryTests: requestedKey != null ? asTestList(data[requestedKey]) : [],
      hasSplitLists: true,
    };
  }

  return {
    customerTests: [],
    directoryTests: [],
    requestedDirectoryTests: [],
    hasSplitLists: false,
  };
};

const normalizeTestSuggestions = (data = {}, category) => {
  const {
    customerTests, directoryTests, hasSplitLists,
  } = readSuggestionLists(data, category);
  if (!hasSplitLists) {
    const raw = data.result ?? data.results;
    return asTestList(raw);
  }
  if (isTestDirectoryCategory(category)) {
    return directoryTests;
  }
  return customerTests;
};

const normalizePagination = (data = {}, page = 1) => {
  const nextPageLink = data.next_page_link || data.next || "";
  const prevPageLink = data.prev_page_link || data.previous || "";
  const totalPages = Number(
    data.total_page ?? data.total_pages ?? data.totalPages ?? 1,
  ) || 1;
  const pageSize = Number(
    data.page_size ?? data.pageSize ?? 10,
  ) || 10;
  const countFromApi = data.count ?? data.total_count ?? data.totalCount;
  const totalCount = countFromApi != null && countFromApi !== ""
    ? Number(countFromApi) || 0
    : totalPages * pageSize;

  return {
    nextPageLink,
    prevPageLink,
    totalPages,
    currentPage: page,
    pageSize,
    totalCount,
  };
};

/** Normalize GET /customertests/ groups ({ name, tests } or { group_name, test_list }). */
const normalizeCustomerGroup = (group = {}) => {
  const groupName = group.group_name || group.name || "";
  const groupId = String(group.group_id ?? group.groupId ?? "").trim();
  let testList = [];
  if (Array.isArray(group.test_list)) {
    testList = group.test_list;
  } else if (Array.isArray(group.tests)) {
    testList = group.tests;
  }
  return {
    ...group,
    ...(groupId ? { group_id: groupId } : {}),
    group_name: groupName,
    name: groupName,
    test_list: testList,
    tests: testList,
    group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    type: group.type || group.group_type || CUSTOMER_GROUP_TYPE,
  };
};

/**
 * Normalize a single POST /testrequest/ item.
 * Contract uses `test_name` (not Postman `requested_name`). Optional type: unlock | new.
 */
const normalizeTestRequestPayload = (request = {}) => {
  const rawType = request.type
    ?? request.request_type
    ?? "";
  const normalizedType = String(rawType).trim().toLowerCase();
  let type;
  if (normalizedType === "unlock" || normalizedType === "unlock tests") {
    type = "unlock";
  } else if (normalizedType === "new" || normalizedType === "new test") {
    type = "new";
  } else if (normalizedType) {
    type = normalizedType;
  } else {
    type = undefined;
  }

  const payload = {
    test_name: request.test_name ?? request.requested_name ?? "",
    test_frequency: request.test_frequency ?? null,
    test_spec: request.test_spec ?? "",
    test_unit: request.test_unit ?? "",
    test_method: request.test_method ?? "",
    comment: request.comment ?? "",
  };
  if (type) {
    payload.type = type;
  }
  return payload;
};

/**
 * Get tests from /testsuggestions/ (BE returns full list; no pagination).
 * @param {String} search search text
 * @param {String} sampleCategory product/environment/product&environment
 * @param {String} [customerID] optional customer id for logged-out flows
 * @param {String} [sort] e.g. recent | az | za
 * @param {String} [category] customer-mapping | test-directory
 * @return {Object} { success, message, tests, customerTests, directoryTests, requestedDirectoryTests }
 */
export const getTestDirectory = async (
  search = "",
  sampleCategory = "product",
  customerID,
  sort,
  category,
) => {
  try {
    const params = {
      search,
      side: sampleCategory,
      display_name_only: "False",
    };
    if (customerID !== undefined) {
      params.customer_id = customerID;
    }
    if (sort) {
      params.sort = sort;
    }
    if (category) {
      params.category = category;
    }

    const resp = await axios.get(`${API_URL}/testsuggestions/`, { params });
    const lists = readSuggestionLists(resp.data, category);
    const tests = normalizeTestSuggestions(resp.data, category);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
      tests,
      customerTests: lists.customerTests,
      directoryTests: lists.directoryTests,
      requestedDirectoryTests: lists.requestedDirectoryTests || [],
    };
  } catch (e) {
    getViewAsPerStatusCode(e);
    return {
      success: false,
      message: "Something went wrong.",
      tests: [],
      customerTests: [],
      directoryTests: [],
      requestedDirectoryTests: [],
    };
  }
};

/** Soft-fail without maintenance redirect (used when /customertests/ or /testrequest/ may be down). */
const softApiFailure = (e, fallbackMessage, extra = {}) => ({
  success: false,
  message: e.response?.data?.message || fallbackMessage,
  ...extra,
});

/**
 * Get customer test groups from /customertests/
 * @param {String} [search] optional search text
 * @param {String} [customerID] optional customer id for logged-out flows
 * @return {Object} { success, message, groups }
 */
export const getCustomerGroups = async (search = "", customerID) => {
  try {
    const params = {};
    if (search) params.search = search;
    if (customerID !== undefined) {
      params.customer_id = customerID;
    }
    const resp = await axios.get(`${API_URL}/customertests/`, { params });
    const result = resp.data.result ?? resp.data.results ?? [];
    let rawGroups = [];
    if (Array.isArray(result)) {
      rawGroups = result;
    } else if (result && typeof result === "object") {
      rawGroups = Array.isArray(result.groups) ? result.groups : [result];
    }
    const groups = rawGroups.map(normalizeCustomerGroup);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
      groups,
    };
  } catch (e) {
    return softApiFailure(e, "Groups are temporarily unavailable.", { groups: [] });
  }
};

/**
 * Create a customer test group via POST /customertests/
 * @param {{ group_name: string, test_list: string[], group_type?: string }} group
 * @return {Object} { success, message, group_id? }
 */
export const createCustomerGroup = async (group) => {
  try {
    const resp = await axios.post(`${API_URL}/customertests/`, {
      action: "create",
      group_name: group.group_name ?? "",
      test_list: Array.isArray(group.test_list) ? group.test_list : [],
      group_type: group.group_type || CUSTOMER_GROUP_TYPE,
    });

    const result = resp.data.result ?? resp.data.results;
    let createdGroupId = "";
    if (result && typeof result === "object" && !Array.isArray(result)) {
      createdGroupId = String(result.group_id ?? result.groupId ?? "").trim();
    } else if (Array.isArray(result) && result[0] && typeof result[0] === "object") {
      createdGroupId = String(result[0].group_id ?? result[0].groupId ?? "").trim();
    }

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
      ...(createdGroupId ? { group_id: createdGroupId } : {}),
    };
  } catch (e) {
    return softApiFailure(e, "Unable to create group. Please try again later.");
  }
};

/**
 * Edit a customer test group via POST /customertests/
 * @param {{ group_id?: string, prev_group?: string, previous_group?: string, group_name: string, test_list: string[], group_type?: string }} group
 * @return {Object} { success, message }
 */
export const updateCustomerGroup = async (group) => {
  try {
    const groupId = String(group.group_id ?? group.groupId ?? "").trim();
    const payload = {
      action: "edit",
      group_name: group.group_name ?? "",
      test_list: Array.isArray(group.test_list) ? group.test_list : [],
      group_type: group.group_type || CUSTOMER_GROUP_TYPE,
    };
    if (groupId) {
      payload.group_id = groupId;
    } else {
      payload.prev_group = group.prev_group
        ?? group.previous_group
        ?? group.group_name
        ?? "";
    }
    const resp = await axios.post(`${API_URL}/customertests/`, payload);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
    };
  } catch (e) {
    return softApiFailure(e, "Unable to update group. Please try again later.");
  }
};

/**
 * Delete a customer test group via POST /customertests/
 * @param {{ group_id?: string, group_name?: string, group_type?: string }} group
 * @return {Object} { success, message }
 */
export const deleteCustomerGroup = async (group) => {
  try {
    const groupId = String(group.group_id ?? group.groupId ?? "").trim();
    const payload = {
      action: "delete",
      group_type: group.group_type || CUSTOMER_GROUP_TYPE,
    };
    if (groupId) {
      payload.group_id = groupId;
    } else {
      payload.group_name = group.group_name ?? "";
    }
    const resp = await axios.post(`${API_URL}/customertests/`, payload);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
    };
  } catch (e) {
    return softApiFailure(e, "Unable to delete group. Please try again later.");
  }
};

/** Normalize GET /testrequest/ items (test_name / request_date aliases). */
const normalizeTestRequest = (request = {}) => {
  const testName = request.test_name || request.requested_name || "";
  const requestDate = request.request_date
    || request.requested_at
    || request.requests_at
    || "";
  return {
    ...request,
    test_name: testName,
    requested_name: request.requested_name || testName,
    request_date: requestDate,
    type: request.type || request.request_type || "",
    request_type: request.request_type || request.type || "",
  };
};

/**
 * Get pending or past customer test requests.
 * @param {"pending"|"history"} status request collection to retrieve
 * @param {String} [search] optional test-name search
 * @param {Number|String} [page=1] page number
 * @return {Object} { success, message, requests, nextPageLink, prevPageLink, totalPages, currentPage, pageSize, totalCount }
 */
export const getTestRequests = async (status, search = "", page = 1) => {
  try {
    const params = { status, page };
    const query = String(search || "").trim();
    if (query) params.search = query;
    const resp = await axios.get(`${API_URL}/testrequest/`, { params });
    const list = readApiResultList(resp.data).map(normalizeTestRequest);
    const pagination = normalizePagination(resp.data, page);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
      requests: list,
      ...pagination,
    };
  } catch (e) {
    return softApiFailure(e, "Test requests are temporarily unavailable.", {
      requests: [],
      nextPageLink: "",
      prevPageLink: "",
      totalPages: 1,
      currentPage: page,
      pageSize: 10,
      totalCount: 0,
    });
  }
};

/**
 * Create one or more test requests via POST /testrequest/
 * @param {Object|Object[]} requests single request or list of requests
 * @return {Object} { success, message }
 */
export const createTestRequests = async (requests) => {
  try {
    const list = Array.isArray(requests) ? requests : [requests];
    const payload = {
      test_requests: list.map(normalizeTestRequestPayload),
    };
    const resp = await axios.post(`${API_URL}/testrequest/`, payload);

    return {
      success: resp.data.message === "Success",
      message: resp.data.message,
    };
  } catch (e) {
    return softApiFailure(e, "Unable to submit test request. Please try again later.");
  }
};
